import { reactive } from "vue";
import { api, ApiError } from "../api/client";
import type { Me, Workspace } from "../api/types";
import { setLocale } from "../i18n";
import { LOCALES } from "@desfecha/i18n";
import { forgetDrafts } from "./composerDrafts";

export const session = reactive<{
  me: Me | null;
  loaded: boolean;
  loading: boolean;
  /**
   * The company open in this tab. Addresses do not carry it (app.donetalk.com/inbox): it is chosen
   * at sign-in or in the workspace list, remembered for the next visit, and API calls use it.
   */
  slug: string;
}>({ me: null, loaded: false, loading: false, slug: "" });

export async function ensureSession(): Promise<boolean> {
  if (session.loaded) return Boolean(session.me);
  session.loading = true;
  try {
    const me = await api<Me>("/v1/auth/me");
    session.me = me;
    if ((LOCALES as readonly string[]).includes(me.user.locale))
      setLocale(me.user.locale as (typeof LOCALES)[number]);
    return true;
  } catch (err) {
    if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
      session.me = null;
      return false;
    }
    throw err;
  } finally {
    session.loaded = true;
    session.loading = false;
  }
}

/** Signed in, or the password was right and the authenticator code comes next. */
export type LoginOutcome =
  { workspaces: Workspace[] } | { challenge: string; expiresInSeconds: number };

export async function login(
  email: string,
  password: string,
): Promise<LoginOutcome> {
  const result = await api<
    | { user: Me["user"]; workspaces: Workspace[] }
    | { mfaRequired: true; challenge: string; expiresInSeconds: number }
  >("/v1/auth/login", { method: "POST", body: { email, password } });
  if ("mfaRequired" in result)
    return {
      challenge: result.challenge,
      expiresInSeconds: result.expiresInSeconds,
    };
  session.loaded = false;
  await ensureSession();
  return { workspaces: result.workspaces };
}

/** The second step: a code from the authenticator app or a recovery code. */
export async function loginWithCode(
  challenge: string,
  code: string,
): Promise<Workspace[]> {
  const result = await api<{ user: Me["user"]; workspaces: Workspace[] }>(
    "/v1/auth/login/code",
    { method: "POST", body: { challenge, code } },
  );
  session.loaded = false;
  await ensureSession();
  return result.workspaces;
}

export async function logout(): Promise<void> {
  await api("/v1/auth/logout", { method: "POST" }).catch(() => undefined);
  forgetDrafts();
  session.me = null;
  session.loaded = true;
}

export function workspace(slug: string): Workspace | null {
  return session.me?.workspaces.find((w) => w.slug === slug) ?? null;
}

/** Opens a company in this tab and remembers it for the next visit. */
export function openWorkspace(slug: string): void {
  session.slug = slug;
  rememberWorkspace(slug);
}

/**
 * The company to open when the address does not say: the one open in this tab, the last one
 * opened, or the only one ready; null when the person has to choose.
 */
export function pickWorkspace(): string | null {
  const ready = session.me?.workspaces.filter((w) => w.ready) ?? [];
  const has = (slug: string | null) =>
    !!slug && ready.some((w) => w.slug === slug);
  if (has(session.slug)) return session.slug;
  const last = lastWorkspace();
  if (has(last)) return last;
  return ready.length === 1 ? ready[0]!.slug : null;
}

const LAST = "donetalk-last-workspace";
export function rememberWorkspace(slug: string): void {
  try {
    localStorage.setItem(LAST, slug);
  } catch {
    /* ignore */
  }
}
export function lastWorkspace(): string | null {
  try {
    return localStorage.getItem(LAST);
  } catch {
    return null;
  }
}
